import * as React from 'react';
import { useIsTouchDevice } from '../hooks/use-is-touch-device';
import { cn } from '../utils/cn';

type TooltipProps = {
  content: string;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  delay?: number;
  disabled?: boolean;
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  delay = 500,
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isTouchDevice = useIsTouchDevice();

  // Automatically disable tooltips on touch devices
  const isDisabled = disabled || isTouchDevice;

  const handleMouseEnter = () => {
    if (isDisabled) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sideClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    center: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent',
    bottom:
      'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent',
    right:
      'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent',
    center: 'hidden', // No arrow for center position
  };

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isVisible && !isDisabled && (
        <div
          className={cn(
            'absolute z-50 pointer-events-none',
            'animate-in fade-in-0 zoom-in-95',
            'duration-200',
            sideClasses[side]
          )}
        >
          <div
            className={cn(
              'px-3 py-1.5 rounded-xl',
              'glass-card-bg glass-card-border glass-tooltip text-popover-foreground',
              'text-xs font-medium whitespace-nowrap'
            )}
          >
            {content}

            {/* Arrow */}
            <div
              className={cn('absolute w-0 h-0', 'border-4 border-popover', arrowClasses[side])}
            />
          </div>
        </div>
      )}
    </div>
  );
};
